import {
  createMedicalInvoice,
  deleteMedicalInvoice,
  getMedicalInvoices,
  updateMedicalInvoice
} from './medical-invoices'
import type { MedicalInvoice, MedicalInvoiceInput, MedicalSpecialty } from '@/types/health'

export type InvoiceImportOrigin = 'summary' | 'document' | 'manual'

export interface ExpenseSummaryImportRow {
  input: Omit<MedicalInvoiceInput, 'patient_id'>
  sheetName: string
  rowIndex: number
  rawRow: Record<string, unknown>
}

export interface ExpenseSummarySheetPreview {
  name: string
  importedRows: number
  skippedRows: number
}

export interface ParsedExpenseSummaryFile {
  fileName: string
  totalRows: number
  skippedRows: number
  sheets: ExpenseSummarySheetPreview[]
  rows: ExpenseSummaryImportRow[]
}

export interface SaveMedicalInvoiceOptions {
  origin: InvoiceImportOrigin
  sourceFileName?: string
  sourceSheetName?: string
  sourceRowIndex?: number
  rawSourceRow?: Record<string, unknown>
  existingInvoices?: MedicalInvoice[]
}

export interface SaveMedicalInvoiceResult {
  action: 'created' | 'updated' | 'duplicate'
  invoice: MedicalInvoice
  matchReason: string | null
}

export interface ExpenseSummaryImportResult {
  totalRows: number
  createdCount: number
  updatedCount: number
  duplicateCount: number
  skippedCount: number
  invoices: MedicalInvoice[]
  errors: string[]
}

type SummaryCanonicalField =
  | 'service_date'
  | 'invoice_date'
  | 'practitioner_name'
  | 'medical_facility'
  | 'specialty'
  | 'total_amount'
  | 'patient_paid_amount'
  | 'secu_reimbursed'
  | 'mutuelle_reimbursed'
  | 'invoice_number'
  | 'notes'
  | 'payment_date'
  | 'is_paid'

type ReconciliationKeys = {
  invoiceNumberKey: string | null
  providerKey: string
  strictSignature: string
  looseSignature: string
  fallbackSignature: string
  dateKey: string
  totalCents: number
}

const SUMMARY_FIELD_ALIASES: Record<SummaryCanonicalField, string[]> = {
  service_date: [
    'date',
    'date soin',
    'date de soin',
    'date soins',
    'date de soins',
    'date consultation',
    'date acte',
    'date depense',
    'date depense medicale',
    'date prestation',
    'date analyse',
    'date acte',
    'date consultation',
    'date intervention',
    'date operation',
    'date debit',
    'date remboursement'
  ],
  invoice_date: [
    'date facture',
    'date de facture',
    'date emission',
    'date d emission',
    'emission',
    'facture date',
    'date reglement',
    'date de reglement',
    'date paiement fournisseur'
  ],
  practitioner_name: [
    'praticien',
    'medecin',
    'docteur',
    'dr',
    'professionnel',
    'professionnel sante',
    'prestataire',
    'fournisseur',
    'nom praticien',
    'nom medecin',
    'beneficiaire',
    'nom professionnel',
    'professionnel de sante',
    'intervenant',
    'nom',
    'tiers'
  ],
  medical_facility: [
    'etablissement',
    'cabinet',
    'centre',
    'clinique',
    'hopital',
    'laboratoire',
    'structure',
    'organisme',
    'lieu',
    'site',
    'adresse',
    'nom etablissement'
  ],
  specialty: [
    'specialite',
    'specialite medicale',
    'categorie',
    'famille',
    'discipline',
    'service',
    'type',
    'type soin',
    'type de soin',
    'famille soin',
    'poste',
    'poste de depense'
  ],
  total_amount: [
    'montant',
    'montant total',
    'total',
    'depense',
    'depense totale',
    'cout',
    'prix',
    'honoraires',
    'facture',
    'somme',
    'montant facture',
    'montant depense',
    'montant ttc',
    'total facture'
  ],
  patient_paid_amount: [
    'reste a charge',
    'part patient',
    'avance',
    'avance patient',
    'debours',
    'debourse',
    'regle',
    'paye',
    'montant paye',
    'paye patient',
    'part assure',
    'reste',
    'solde patient',
    'rac'
  ],
  secu_reimbursed: [
    'secu',
    'securite sociale',
    'remboursement secu',
    'part secu',
    'cpam',
    'ameli',
    'ro',
    'regime obligatoire',
    'part obligatoire',
    'remboursement ameli'
  ],
  mutuelle_reimbursed: [
    'mutuelle',
    'complementaire',
    'remboursement mutuelle',
    'part mutuelle',
    'rc',
    'part complementaire',
    'remboursement complementaire'
  ],
  invoice_number: [
    'numero facture',
    'num facture',
    'facture n',
    'numero',
    'reference',
    'ref',
    'n facture',
    'n de facture',
    'numero dossier',
    'numero decompte'
  ],
  notes: [
    'description',
    'libelle',
    'designation',
    'acte',
    'motif',
    'commentaire',
    'details',
    'nature',
    'observation',
    'intitule',
    'poste libelle',
    'nature soin',
    'nature depense',
    'libelle acte'
  ],
  payment_date: [
    'date paiement',
    'date de paiement',
    'paye le',
    'regle le',
    'date reglement',
    'date de reglement'
  ],
  is_paid: ['statut', 'paiement', 'payee', 'reglee', 'etat', 'reglement']
}

const TRUE_TOKENS = new Set(['oui', 'true', '1', 'ok', 'paye', 'payee', 'regle', 'reglee', 'paid'])
const FALSE_TOKENS = new Set(['non', 'false', '0', 'impaye', 'impayee', 'pending', 'en attente'])
const GENERIC_PROVIDER_WORDS = new Set([
  'dr',
  'docteur',
  'doctoresse',
  'doct',
  'cabinet',
  'centre',
  'clinique',
  'hopital',
  'hospital',
  'laboratoire',
  'labo',
  'pharmacie',
  'madame',
  'monsieur',
  'mme',
  'mr',
  'm',
  'scp',
  'selarl',
  'sarl',
  'sas',
  'eurl',
  'consultation',
  'honoraires',
  'analyse',
  'medical',
  'medicaux'
])
const CONTEXT_NOISE_WORDS = new Set([
  'message',
  'journal',
  'quotidien',
  'hebdomadaire',
  'mensuel',
  'annuel',
  'semaine',
  'mois',
  'annee',
  'probleme',
  'problemes',
  'arret',
  'arrets',
  'donnee',
  'donnees',
  'statut',
  'suivi',
  'tableau',
  'ligne',
  'lignes',
  'resume',
  'recap',
  'remboursement',
  'remboursements',
  'cpam',
  'ameli',
  'mutuelle',
  'solde',
  'cumul',
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche'
])
const EXPENSE_SHEET_HINTS = [
  'facture',
  'depense',
  'frais',
  'soin',
  'consult',
  'rembourse',
  'mutuelle',
  'secu',
  'cpam',
  'ameli'
]
const MAX_REASONABLE_INVOICE_AMOUNT = 50000

function removeDiacritics(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function normalizeText(value: string): string {
  return removeDiacritics(value)
    .toLowerCase()
    .replace(/['\u2019]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function normalizeHeaderLabel(value: string): string {
  return normalizeText(value)
}

function tokenizeNormalizedText(value: string | null | undefined): string[] {
  return normalizeText(value || '')
    .split(' ')
    .filter(Boolean)
}

function isNumericLikeText(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }

  return /^[\d\s.,/()-]+$/.test(value.trim())
}

function isMeaningfulContextText(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }

  if (!/[a-z]/i.test(removeDiacritics(value))) {
    return false
  }

  if (isNumericLikeText(value)) {
    return false
  }

  const informativeTokens = tokenizeNormalizedText(value).filter(
    (token) =>
      token.length >= 3 &&
      !GENERIC_PROVIDER_WORDS.has(token) &&
      !CONTEXT_NOISE_WORDS.has(token) &&
      !/^\d+$/.test(token)
  )

  return informativeTokens.length > 0
}

function chooseMeaningfulContext(...values: Array<string | null | undefined>): string | null {
  return values.map((value) => value?.trim() || null).find((value) => isMeaningfulContextText(value)) || null
}

function isLikelyExpenseSheetName(sheetName: string): boolean {
  const normalized = normalizeText(sheetName)
  return EXPENSE_SHEET_HINTS.some((hint) => normalized.includes(hint))
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])]
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100
}

function toDateOnlyString(date: Date): string {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseExcelSerialDate(value: number): string | null {
  if (
    !Number.isFinite(value) ||
    value < 20000 ||
    value > 80000
  ) {
    return null
  }

  const utcEpoch = Date.UTC(1899, 11, 30)
  const date = new Date(utcEpoch + Math.round(value * 86400000))
  return Number.isNaN(date.getTime()) ? null : toDateOnlyString(date)
}

function parseFrenchDateString(value: string): string | null {
  const trimmed = value.trim()

  if (!trimmed) {
    return null
  }

  const normalized = removeDiacritics(trimmed.toLowerCase())
  const monthMap: Record<string, string> = {
    janvier: '01',
    janv: '01',
    fevrier: '02',
    fevr: '02',
    mars: '03',
    avril: '04',
    avr: '04',
    mai: '05',
    juin: '06',
    juillet: '07',
    juil: '07',
    aout: '08',
    sept: '09',
    septembre: '09',
    oct: '10',
    octobre: '10',
    nov: '11',
    novembre: '11',
    dec: '12',
    decembre: '12'
  }

  for (const [monthName, monthNumber] of Object.entries(monthMap)) {
    if (normalized.includes(monthName)) {
      const replaced = normalized.replace(monthName, monthNumber)
      const match = replaced.match(/(\d{1,2})\s+(\d{2})\s+(\d{2,4})/)

      if (match) {
        const day = match[1].padStart(2, '0')
        const month = match[2]
        const year = match[3].length === 2 ? `20${match[3]}` : match[3]
        return `${year}-${month}-${day}`
      }
    }
  }

  const directMatch = normalized.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/)

  if (directMatch) {
    const day = directMatch[1].padStart(2, '0')
    const month = directMatch[2].padStart(2, '0')
    const year = directMatch[3].length === 2 ? `20${directMatch[3]}` : directMatch[3]
    return `${year}-${month}-${day}`
  }

  const isoMatch = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)

  if (isoMatch) {
    const month = isoMatch[2].padStart(2, '0')
    const day = isoMatch[3].padStart(2, '0')
    return `${isoMatch[1]}-${month}-${day}`
  }

  const parsed = Date.parse(trimmed)
  if (!Number.isNaN(parsed)) {
    return toDateOnlyString(new Date(parsed))
  }

  return null
}

function parseDateValue(value: unknown): string | null {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : toDateOnlyString(value)
  }

  if (typeof value === 'number') {
    return parseExcelSerialDate(value)
  }

  if (typeof value === 'string') {
    return parseFrenchDateString(value)
  }

  return null
}

function parseAmountValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return roundCurrency(value)
  }

  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()

  if (!trimmed) {
    return null
  }

  const normalized = trimmed
    .replace(/[\u20ac\s\u00a0]/g, '')
    .replace(/\((.+)\)/, '-$1')
    .replace(/[^0-9,.-]/g, '')

  if (!normalized) {
    return null
  }

  const commaCount = (normalized.match(/,/g) || []).length
  const dotCount = (normalized.match(/\./g) || []).length

  let sanitized = normalized

  if (commaCount > 0 && dotCount > 0) {
    sanitized =
      normalized.lastIndexOf(',') > normalized.lastIndexOf('.')
        ? normalized.replace(/\./g, '').replace(',', '.')
        : normalized.replace(/,/g, '')
  } else if (commaCount > 0) {
    sanitized = normalized.replace(/\./g, '').replace(',', '.')
  }

  const parsed = Number.parseFloat(sanitized)
  return Number.isFinite(parsed) ? roundCurrency(parsed) : null
}

function maybeText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || null
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return null
}

function inferBooleanValue(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value
  }

  const text = normalizeText(maybeText(value) || '')

  if (!text) {
    return null
  }

  if (TRUE_TOKENS.has(text)) {
    return true
  }

  if (FALSE_TOKENS.has(text)) {
    return false
  }

  return null
}

function resolveCanonicalField(header: string): SummaryCanonicalField | null {
  const normalizedHeader = normalizeHeaderLabel(header)

  for (const [field, aliases] of Object.entries(SUMMARY_FIELD_ALIASES)) {
    if (aliases.some((alias) => normalizedHeader === alias || normalizedHeader.includes(alias))) {
      return field as SummaryCanonicalField
    }
  }

  return null
}

function scoreHeaderRow(row: unknown[]): number {
  const recognized = new Set<SummaryCanonicalField>()

  row.forEach((cell) => {
    const text = maybeText(cell)
    if (!text) {
      return
    }

    const resolved = resolveCanonicalField(text)
    if (resolved) {
      recognized.add(resolved)
    }
  })

  return recognized.size
}

function makeUniqueKey(base: string, seen: Map<string, number>): string {
  const current = seen.get(base) || 0
  seen.set(base, current + 1)
  return current === 0 ? base : `${base}__${current + 1}`
}

function buildStructuredRow(headers: string[], rowValues: unknown[]): {
  mappedRow: Record<string, unknown>
  rawRow: Record<string, unknown>
} {
  const seen = new Map<string, number>()
  const mappedRow: Record<string, unknown> = {}
  const rawRow: Record<string, unknown> = {}

  headers.forEach((header, index) => {
    const safeHeader = header || `colonne_${index + 1}`
    const rawValue = rowValues[index]
    const canonicalField = resolveCanonicalField(safeHeader)
    const rawKey = makeUniqueKey(safeHeader, seen)
    rawRow[rawKey] = rawValue

    if (canonicalField && mappedRow[canonicalField] === undefined) {
      mappedRow[canonicalField] = rawValue
    }
  })

  return { mappedRow, rawRow }
}

function isProbablyTotalsRow(rowValues: unknown[]): boolean {
  const joined = normalizeText(
    rowValues
      .map((value) => maybeText(value))
      .filter(Boolean)
      .join(' ')
  )

  return Boolean(joined) && /(total|sous total|solde|cumul|hebdomadaire|mensuel|annuel|resume|recap)/.test(joined)
}

function isRepeatedHeaderRow(rowValues: unknown[]): boolean {
  return scoreHeaderRow(rowValues) >= 3
}

function compactSourceRow(rawRow: Record<string, unknown>): Record<string, unknown> {
  const compactEntries = Object.entries(rawRow)
    .filter(([, value]) => {
      const text = maybeText(value)
      if (text !== null) {
        return text.length > 0
      }

      return typeof value === 'number' || typeof value === 'boolean'
    })
    .slice(0, 12)

  return Object.fromEntries(compactEntries)
}

type RowCandidate = {
  key: string
  normalizedKey: string
  rawValue: unknown
  textValue: string | null
  normalizedText: string
  dateValue: string | null
  amountValue: number | null
}

const PROVIDER_HEADER_HINTS = [
  'praticien',
  'professionnel',
  'medecin',
  'docteur',
  'prestataire',
  'intervenant',
  'fournisseur',
  'nom'
]

const FACILITY_HEADER_HINTS = [
  'etablissement',
  'cabinet',
  'centre',
  'clinique',
  'hopital',
  'laboratoire',
  'lieu',
  'organisme',
  'structure'
]

const TOTAL_AMOUNT_HEADER_HINTS = ['montant', 'total', 'ttc', 'facture', 'depense', 'somme']
const PATIENT_AMOUNT_HEADER_HINTS = ['reste', 'patient', 'charge', 'rac', 'paye', 'regle']
const SECU_AMOUNT_HEADER_HINTS = ['secu', 'cpam', 'ameli', 'ro', 'obligatoire']
const MUTUELLE_AMOUNT_HEADER_HINTS = ['mutuelle', 'complementaire', 'rc']
const NOTES_HEADER_HINTS = ['description', 'libelle', 'designation', 'motif', 'details', 'nature', 'acte', 'intitule']
const SPECIALTY_HEADER_HINTS = ['specialite', 'categorie', 'famille', 'discipline', 'service', 'type', 'poste']
const INVOICE_NUMBER_HEADER_HINTS = ['facture', 'reference', 'ref', 'numero', 'decompte', 'dossier']
const INVOICE_DATE_HEADER_HINTS = ['facture', 'emission']
const SERVICE_DATE_HEADER_HINTS = ['date', 'soin', 'prestation', 'consultation', 'analyse', 'intervention']

function collectRowCandidates(rawRow: Record<string, unknown>): RowCandidate[] {
  return Object.entries(rawRow).map(([key, rawValue]) => {
    const textValue = maybeText(rawValue)

    return {
      key,
      normalizedKey: normalizeHeaderLabel(key),
      rawValue,
      textValue,
      normalizedText: normalizeText(textValue || ''),
      dateValue: parseDateValue(rawValue),
      amountValue: parseAmountValue(rawValue)
    }
  })
}

function headerMatchesAnyHint(candidate: RowCandidate, hints: string[]): boolean {
  return hints.some((hint) => candidate.normalizedKey.includes(hint))
}

function isUsefulTextCandidate(candidate: RowCandidate): boolean {
  if (!candidate.textValue) {
    return false
  }

  if (candidate.dateValue || candidate.amountValue !== null) {
    return false
  }

  return isMeaningfulContextText(candidate.textValue)
}

function looksLikeFacilityName(value: string): boolean {
  const normalized = normalizeText(value)
  return ['cabinet', 'centre', 'clinique', 'hopital', 'laboratoire', 'labo', 'pharmacie']
    .some((token) => normalized.includes(token))
}

function pickTextByHints(
  candidates: RowCandidate[],
  hints: string[],
  excludedTexts: string[] = []
): string | null {
  const excluded = new Set(excludedTexts.map((value) => normalizeText(value)))

  const match = candidates
    .filter((candidate) => isUsefulTextCandidate(candidate) && headerMatchesAnyHint(candidate, hints))
    .sort((left, right) => (right.textValue?.length || 0) - (left.textValue?.length || 0))
    .find((candidate) => !excluded.has(candidate.normalizedText))

  return match?.textValue || null
}

function pickFallbackText(
  candidates: RowCandidate[],
  excludedTexts: string[] = []
): string | null {
  const excluded = new Set(excludedTexts.map((value) => normalizeText(value)))

  const match = candidates
    .filter((candidate) => isUsefulTextCandidate(candidate))
    .filter((candidate) => !excluded.has(candidate.normalizedText))
    .filter((candidate) => !GENERIC_PROVIDER_WORDS.has(candidate.normalizedText))
    .sort((left, right) => (right.textValue?.length || 0) - (left.textValue?.length || 0))[0]

  return match?.textValue || null
}

function pickDateByHints(candidates: RowCandidate[], hints: string[]): string | null {
  return (
    candidates.find(
      (candidate) => candidate.dateValue && headerMatchesAnyHint(candidate, hints)
    )?.dateValue || null
  )
}

function pickAmountByHints(candidates: RowCandidate[], hints: string[]): number | null {
  const match = candidates
    .filter((candidate) => candidate.amountValue !== null)
    .find((candidate) => headerMatchesAnyHint(candidate, hints))

  return match?.amountValue ?? null
}

function getDistinctDates(candidates: RowCandidate[]): string[] {
  return [...new Set(candidates.map((candidate) => candidate.dateValue).filter(Boolean) as string[])]
}

function getPositiveAmounts(candidates: RowCandidate[]): number[] {
  return candidates
    .map((candidate) => candidate.amountValue)
    .filter((value): value is number => typeof value === 'number' && value > 0)
}

function buildSyntheticHeaders(columnCount: number): string[] {
  return Array.from({ length: columnCount }, (_, index) => `colonne_${index + 1}`)
}

function looksLikeInvoiceDataRow(rowValues: unknown[]): boolean {
  if (isProbablyTotalsRow(rowValues)) {
    return false
  }

  const dateCount = rowValues.filter((value) => parseDateValue(value) !== null).length
  const amountCount = rowValues.filter((value) => {
    const amount = parseAmountValue(value)
    return amount !== null && amount > 0 && amount <= MAX_REASONABLE_INVOICE_AMOUNT
  }).length
  const textCount = rowValues.filter((value) => {
    const text = maybeText(value)
    return Boolean(text) &&
      parseDateValue(value) === null &&
      parseAmountValue(value) === null &&
      isMeaningfulContextText(text)
  }).length

  return dateCount >= 1 && amountCount >= 1 && textCount >= 1
}

function getMaxColumnCount(matrix: unknown[][]): number {
  return matrix.reduce((max, row) => Math.max(max, row?.length || 0), 0)
}

function countLikelyInvoiceRows(
  matrix: unknown[][],
  dataStartIndex: number,
  scanLimit = 30
): number {
  let likelyRows = 0
  let scannedRows = 0

  for (let index = dataStartIndex; index < matrix.length && scannedRows < scanLimit; index++) {
    const rowValues = matrix[index]

    if (!rowValues || rowValues.every((value) => maybeText(value) === null && typeof value !== 'number')) {
      continue
    }

    if (isRepeatedHeaderRow(rowValues) || isProbablyTotalsRow(rowValues)) {
      continue
    }

    scannedRows += 1

    if (looksLikeInvoiceDataRow(rowValues)) {
      likelyRows += 1
    }
  }

  return likelyRows
}

function inferMedicalSpecialty(...values: Array<string | null | undefined>): MedicalSpecialty {
  const haystack = normalizeText(values.filter(Boolean).join(' '))

  if (!haystack) {
    return 'other'
  }

  const specialtyMap: Array<[MedicalSpecialty, string[]]> = [
    ['laboratory', ['laboratoire', 'labo', 'biologie', 'prise de sang', 'analyse']],
    ['radiology', ['radiologie', 'imagerie', 'irm', 'scanner', 'echographie', 'radio']],
    ['pharmacy', ['pharmacie', 'medicament', 'ordonnance']],
    ['gastroenterology', ['gastro', 'digestif', 'coloscopie', 'endoscopie', 'rch', 'crohn']],
    ['gynecology', ['gyneco', 'gynecologie', 'endometriose', 'sage femme']],
    ['physiotherapy', ['kine', 'kinesitherapie', 'reeducation', 'physio']],
    ['psychology', ['psychologue', 'psychologie', 'psy', 'psycho']],
    ['dermatology', ['dermato', 'dermatologie', 'peau']],
    ['cardiology', ['cardio', 'cardiologie', 'coeur', 'ecg']],
    ['endocrinology', ['endocrino', 'endocrinologie', 'thyroide', 'diabete']],
    ['rheumatology', ['rhumato', 'rhumatologie', 'articulation']],
    ['neurology', ['neuro', 'neurologie', 'migraine', 'epilepsie']],
    ['surgery', ['chirurgie', 'chirurgical', 'operatoire', 'operation']],
    ['emergency', ['urgence', 'urgences', 'samu']],
    ['general_medicine', ['generaliste', 'medecin traitant', 'medecine generale', 'consultation']]
  ]

  for (const [specialty, keywords] of specialtyMap) {
    if (keywords.some((keyword) => haystack.includes(keyword))) {
      return specialty
    }
  }

  return 'other'
}

function normalizeInvoiceNumber(value: string | null | undefined): string | null {
  const text = maybeText(value)
  if (!text) {
    return null
  }

  const normalized = normalizeText(text).replace(/\s+/g, '')
  return normalized || null
}

function isReasonableInvoiceAmount(value: number | null | undefined): value is number {
  return typeof value === 'number' && value > 0 && value <= MAX_REASONABLE_INVOICE_AMOUNT
}

function normalizeSummaryContextFields(args: {
  practitionerName?: string | null
  medicalFacility?: string | null
  description?: string | null
}) {
  let practitionerName = args.practitionerName?.trim() || null
  let medicalFacility = args.medicalFacility?.trim() || null
  let description = args.description?.trim() || null

  const fallbackLabel = chooseMeaningfulContext(practitionerName, medicalFacility, description)

  if (!fallbackLabel) {
    return null
  }

  if (!isMeaningfulContextText(practitionerName)) {
    practitionerName = fallbackLabel
  }

  if (medicalFacility && !isMeaningfulContextText(medicalFacility) && !looksLikeFacilityName(medicalFacility)) {
    medicalFacility = null
  }

  if (!medicalFacility && practitionerName && looksLikeFacilityName(practitionerName)) {
    medicalFacility = practitionerName
  }

  if (description && !isMeaningfulContextText(description)) {
    description = null
  }

  if (description && practitionerName && normalizeText(description) === normalizeText(practitionerName)) {
    description = null
  }

  return {
    practitionerName,
    medicalFacility,
    description
  }
}

function buildProviderKey(
  practitionerName: string | null | undefined,
  facilityName: string | null | undefined
): string {
  const combined = uniqueStrings([practitionerName, facilityName])
    .map((value) => normalizeText(value))
    .join(' ')

  if (!combined) {
    return ''
  }

  const tokens = combined
    .split(' ')
    .filter((token) => token && !GENERIC_PROVIDER_WORDS.has(token))

  return [...new Set(tokens)].join(' ')
}

function buildReconciliationKeys(
  input: Partial<Pick<MedicalInvoiceInput, 'invoice_number' | 'practitioner_name' | 'medical_facility' | 'service_date' | 'invoice_date' | 'total_amount' | 'specialty'>>
): ReconciliationKeys {
  const dateKey = input.service_date || input.invoice_date || ''
  const totalCents = Math.round((input.total_amount || 0) * 100)
  const providerKey = buildProviderKey(input.practitioner_name, input.medical_facility)
  const specialty = input.specialty || 'other'
  const invoiceNumberKey = normalizeInvoiceNumber(input.invoice_number)

  return {
    invoiceNumberKey,
    providerKey,
    strictSignature: `${dateKey}|${totalCents}|${providerKey}|${specialty}`,
    looseSignature: `${dateKey}|${totalCents}|${providerKey}`,
    fallbackSignature: `${dateKey}|${totalCents}`,
    dateKey,
    totalCents
  }
}

function extractMetadata(record: Partial<MedicalInvoice>): Record<string, unknown> {
  return record.metadata && typeof record.metadata === 'object' ? record.metadata : {}
}

function getStoredKeys(invoice: MedicalInvoice): ReconciliationKeys {
  const metadata = extractMetadata(invoice)
  const computedKeys = buildReconciliationKeys(invoice)

  return {
    ...computedKeys,
    invoiceNumberKey:
      typeof metadata.invoice_number_key_v1 === 'string'
        ? metadata.invoice_number_key_v1
        : computedKeys.invoiceNumberKey,
    providerKey:
      typeof metadata.provider_key_v1 === 'string'
        ? metadata.provider_key_v1
        : computedKeys.providerKey,
    strictSignature:
      typeof metadata.reconciliation_signature_v1 === 'string'
        ? metadata.reconciliation_signature_v1
        : computedKeys.strictSignature,
    looseSignature:
      typeof metadata.reconciliation_loose_signature_v1 === 'string'
        ? metadata.reconciliation_loose_signature_v1
        : computedKeys.looseSignature,
    fallbackSignature:
      typeof metadata.reconciliation_fallback_signature_v1 === 'string'
        ? metadata.reconciliation_fallback_signature_v1
        : computedKeys.fallbackSignature
  }
}

function overlapScore(left: string, right: string): number {
  if (!left || !right) {
    return 0
  }

  if (left === right) {
    return 1
  }

  const leftTokens = new Set(left.split(' ').filter(Boolean))
  const rightTokens = new Set(right.split(' ').filter(Boolean))
  const intersectionSize = [...leftTokens].filter((token) => rightTokens.has(token)).length
  const unionSize = new Set([...leftTokens, ...rightTokens]).size
  return unionSize > 0 ? intersectionSize / unionSize : 0
}

function findMatchingInvoice(
  existingInvoices: MedicalInvoice[],
  input: MedicalInvoiceInput
): { invoice: MedicalInvoice; reason: string } | null {
  const inputKeys = buildReconciliationKeys(input)
  let bestMatch: { invoice: MedicalInvoice; reason: string; score: number } | null = null

  for (const invoice of existingInvoices) {
    const existingKeys = getStoredKeys(invoice)
    let score = 0
    let reason = 'same-date-amount'

    if (
      inputKeys.invoiceNumberKey &&
      existingKeys.invoiceNumberKey &&
      inputKeys.invoiceNumberKey === existingKeys.invoiceNumberKey
    ) {
      score += 120
      reason = 'invoice-number'
    }

    if (inputKeys.dateKey && inputKeys.dateKey === existingKeys.dateKey) {
      score += 30
    } else {
      continue
    }

    if (inputKeys.totalCents === existingKeys.totalCents) {
      score += 30
    } else {
      continue
    }

    if (inputKeys.strictSignature === existingKeys.strictSignature) {
      score += 50
      reason = 'strict-signature'
    } else if (inputKeys.looseSignature === existingKeys.looseSignature) {
      score += 35
      reason = 'date-amount-provider'
    } else if (inputKeys.fallbackSignature === existingKeys.fallbackSignature) {
      score += 10
    }

    const providerScore = overlapScore(inputKeys.providerKey, existingKeys.providerKey)
    if (providerScore >= 0.9) {
      score += 35
      reason = reason === 'same-date-amount' ? 'provider-match' : reason
    } else if (providerScore >= 0.6) {
      score += 20
    } else if (input.specialty !== 'other' && input.specialty === invoice.specialty) {
      score += 10
    }

    if (bestMatch === null || score > bestMatch.score) {
      bestMatch = { invoice, reason, score }
    }
  }

  return bestMatch && bestMatch.score >= 70
    ? { invoice: bestMatch.invoice, reason: bestMatch.reason }
    : null
}

function chooseStringValue(
  existingValue: string | null | undefined,
  incomingValue: string | null | undefined,
  preferIncoming: boolean
): string | null {
  const safeExisting = maybeText(existingValue)
  const safeIncoming = maybeText(incomingValue)

  if (!safeIncoming) {
    return safeExisting
  }

  if (!safeExisting) {
    return safeIncoming
  }

  if (safeExisting === safeIncoming) {
    return safeExisting
  }

  if (preferIncoming) {
    return safeIncoming
  }

  return safeExisting.length >= safeIncoming.length ? safeExisting : safeIncoming
}

function chooseNumericValue(
  existingValue: number | null | undefined,
  incomingValue: number | null | undefined,
  preferIncoming: boolean
): number | null {
  const safeExisting = typeof existingValue === 'number' ? existingValue : null
  const safeIncoming = typeof incomingValue === 'number' ? incomingValue : null

  if (safeIncoming === null) {
    return safeExisting
  }

  if (safeExisting === null) {
    return safeIncoming
  }

  if (preferIncoming) {
    return safeIncoming
  }

  return Math.max(safeExisting, safeIncoming)
}

function chooseBooleanValue(
  existingValue: boolean | null | undefined,
  incomingValue: boolean | null | undefined
): boolean {
  return Boolean(existingValue || incomingValue)
}

function mergeMetadata(
  existingInvoice: MedicalInvoice | null,
  input: MedicalInvoiceInput,
  options: SaveMedicalInvoiceOptions
): Record<string, unknown> {
  const existingMetadata = existingInvoice ? extractMetadata(existingInvoice) : {}
  const incomingMetadata =
    input.metadata && typeof input.metadata === 'object' ? input.metadata : {}
  const keys = buildReconciliationKeys(input)
  const importSources = new Set<string>()

  const existingImportSources = existingMetadata.import_sources
  if (Array.isArray(existingImportSources)) {
    existingImportSources.forEach((value) => {
      if (typeof value === 'string') {
        importSources.add(value)
      }
    })
  }

  importSources.add(options.origin)

  const hasExistingRealInvoice = Boolean(existingInvoice?.document_url) ||
    existingMetadata.has_real_invoice === true ||
    existingMetadata.is_summary_only === false

  const hasIncomingRealInvoice = Boolean(input.document_url) || options.origin !== 'summary'

  const summaryFileNames = new Set<string>()
  const existingSummaryFiles = existingMetadata.source_summary_file_names
  if (Array.isArray(existingSummaryFiles)) {
    existingSummaryFiles.forEach((value) => {
      if (typeof value === 'string') {
        summaryFileNames.add(value)
      }
    })
  }

  if (options.origin === 'summary' && options.sourceFileName) {
    summaryFileNames.add(options.sourceFileName)
  }

  const nextSummaryImportCount =
    (typeof existingMetadata.summary_import_count === 'number'
      ? existingMetadata.summary_import_count
      : 0) + (options.origin === 'summary' ? 1 : 0)

  return {
    ...existingMetadata,
    ...incomingMetadata,
    import_origin: options.origin,
    import_sources: [...importSources],
    is_summary_only: !hasExistingRealInvoice && !hasIncomingRealInvoice,
    has_real_invoice: hasExistingRealInvoice || hasIncomingRealInvoice,
    reconciliation_signature_v1: keys.strictSignature,
    reconciliation_loose_signature_v1: keys.looseSignature,
    reconciliation_fallback_signature_v1: keys.fallbackSignature,
    invoice_number_key_v1: keys.invoiceNumberKey,
    provider_key_v1: keys.providerKey,
    last_reconciled_at: new Date().toISOString(),
    source_file_name: options.sourceFileName || existingMetadata.source_file_name || null,
    source_sheet_name: options.sourceSheetName || existingMetadata.source_sheet_name || null,
    source_row_index:
      options.sourceRowIndex ?? (typeof existingMetadata.source_row_index === 'number' ? existingMetadata.source_row_index : null),
    source_row_preview:
      options.rawSourceRow ? compactSourceRow(options.rawSourceRow) : existingMetadata.source_row_preview || null,
    source_summary_file_names: [...summaryFileNames],
    summary_import_count: nextSummaryImportCount,
    reconciliation_match_reason:
      existingInvoice && options.origin !== 'summary'
        ? 'matched-summary-entry'
        : existingInvoice
          ? 'matched-existing-invoice'
          : null
  }
}

function buildMergedInvoiceInput(
  existingInvoice: MedicalInvoice,
  input: MedicalInvoiceInput,
  options: SaveMedicalInvoiceOptions
): Partial<MedicalInvoiceInput> {
  const incomingIsSummary = options.origin === 'summary'
  const preferIncoming = !incomingIsSummary || extractMetadata(existingInvoice).is_summary_only === true

  return {
    invoice_number: chooseStringValue(existingInvoice.invoice_number, input.invoice_number, preferIncoming),
    practitioner_name:
      chooseStringValue(existingInvoice.practitioner_name, input.practitioner_name, preferIncoming) ||
      existingInvoice.practitioner_name,
    medical_facility: chooseStringValue(existingInvoice.medical_facility, input.medical_facility, preferIncoming),
    specialty:
      existingInvoice.specialty === 'other' && input.specialty !== 'other'
        ? input.specialty
        : preferIncoming
          ? input.specialty
          : existingInvoice.specialty,
    service_date: chooseStringValue(existingInvoice.service_date, input.service_date, preferIncoming) || existingInvoice.service_date,
    invoice_date: chooseStringValue(existingInvoice.invoice_date, input.invoice_date, preferIncoming) || existingInvoice.invoice_date,
    total_amount: chooseNumericValue(existingInvoice.total_amount, input.total_amount, preferIncoming) || existingInvoice.total_amount,
    patient_paid_amount: chooseNumericValue(existingInvoice.patient_paid_amount, input.patient_paid_amount, false) || 0,
    is_paid: chooseBooleanValue(existingInvoice.is_paid, input.is_paid),
    payment_date: chooseStringValue(existingInvoice.payment_date, input.payment_date, false),
    payment_method: chooseStringValue(existingInvoice.payment_method, input.payment_method, preferIncoming),
    secu_base: chooseNumericValue(existingInvoice.secu_base, input.secu_base, false),
    secu_rate: chooseNumericValue(existingInvoice.secu_rate, input.secu_rate, false),
    secu_reimbursed: chooseNumericValue(existingInvoice.secu_reimbursed, input.secu_reimbursed, false),
    mutuelle_reimbursed: chooseNumericValue(existingInvoice.mutuelle_reimbursed, input.mutuelle_reimbursed, false),
    document_url: chooseStringValue(existingInvoice.document_url, input.document_url, true),
    notes: chooseStringValue(existingInvoice.notes, input.notes, preferIncoming),
    metadata: mergeMetadata(existingInvoice, input, options)
  }
}

function collectChangedFields(
  existingInvoice: MedicalInvoice,
  mergedInput: Partial<MedicalInvoiceInput>
): Partial<MedicalInvoiceInput> {
  const changedFields: Partial<MedicalInvoiceInput> = {}

  const comparableKeys: Array<keyof MedicalInvoiceInput> = [
    'invoice_number',
    'practitioner_name',
    'medical_facility',
    'specialty',
    'service_date',
    'invoice_date',
    'total_amount',
    'patient_paid_amount',
    'is_paid',
    'payment_date',
    'payment_method',
    'secu_base',
    'secu_rate',
    'secu_reimbursed',
    'mutuelle_reimbursed',
    'document_url',
    'notes',
    'metadata'
  ]

  comparableKeys.forEach((key) => {
    const nextValue = mergedInput[key]
    const currentValue = existingInvoice[key as keyof MedicalInvoice]

    if (key === 'metadata') {
      if (JSON.stringify(nextValue || {}) !== JSON.stringify(currentValue || {})) {
        changedFields[key] = nextValue
      }
      return
    }

    if (nextValue !== currentValue && nextValue !== undefined) {
      changedFields[key] = nextValue as never
    }
  })

  return changedFields
}

async function fetchAllMedicalInvoices(patientId: string): Promise<MedicalInvoice[]> {
  return getMedicalInvoices(patientId, undefined, null)
}

function buildBaseMetadata(
  input: MedicalInvoiceInput,
  options: SaveMedicalInvoiceOptions
): Record<string, unknown> {
  const keys = buildReconciliationKeys(input)
  const inputMetadata =
    input.metadata && typeof input.metadata === 'object' ? input.metadata : {}

  return {
    ...inputMetadata,
    import_origin: options.origin,
    import_sources: [options.origin],
    is_summary_only: options.origin === 'summary' && !input.document_url,
    has_real_invoice: Boolean(input.document_url) || options.origin !== 'summary',
    reconciliation_signature_v1: keys.strictSignature,
    reconciliation_loose_signature_v1: keys.looseSignature,
    reconciliation_fallback_signature_v1: keys.fallbackSignature,
    invoice_number_key_v1: keys.invoiceNumberKey,
    provider_key_v1: keys.providerKey,
    source_file_name: options.sourceFileName || null,
    source_sheet_name: options.sourceSheetName || null,
    source_row_index: options.sourceRowIndex ?? null,
    source_row_preview: options.rawSourceRow ? compactSourceRow(options.rawSourceRow) : null,
    source_summary_file_names:
      options.origin === 'summary' && options.sourceFileName ? [options.sourceFileName] : [],
    summary_import_count: options.origin === 'summary' ? 1 : 0,
    last_reconciled_at: new Date().toISOString()
  }
}

function buildSummaryInvoiceInput(
  row: Record<string, unknown>
): Omit<MedicalInvoiceInput, 'patient_id'> | null {
  const serviceDate = parseDateValue(row.service_date) || parseDateValue(row.invoice_date)
  const invoiceDate = parseDateValue(row.invoice_date) || serviceDate

  if (!serviceDate || !invoiceDate) {
    return null
  }

  const practitionerName = maybeText(row.practitioner_name)
  const medicalFacility = maybeText(row.medical_facility)
  const description = maybeText(row.notes)
  const totalAmount =
    parseAmountValue(row.total_amount) ??
    roundCurrency(
      (parseAmountValue(row.patient_paid_amount) || 0) +
      (parseAmountValue(row.secu_reimbursed) || 0) +
      (parseAmountValue(row.mutuelle_reimbursed) || 0)
    )

  if (!isReasonableInvoiceAmount(totalAmount)) {
    return null
  }

  const normalizedContext = normalizeSummaryContextFields({
    practitionerName,
    medicalFacility,
    description
  })

  if (!normalizedContext) {
    return null
  }

  const secuReimbursed = parseAmountValue(row.secu_reimbursed) || 0
  const mutuelleReimbursed = parseAmountValue(row.mutuelle_reimbursed) || 0
  const patientPaidAmount =
    parseAmountValue(row.patient_paid_amount) ??
    roundCurrency(Math.max(totalAmount - secuReimbursed - mutuelleReimbursed, 0))

  const paidFlag = inferBooleanValue(row.is_paid)
  const specialty = inferMedicalSpecialty(
    maybeText(row.specialty),
    normalizedContext.practitionerName,
    normalizedContext.medicalFacility,
    normalizedContext.description
  )

  return {
    practitioner_name: normalizedContext.practitionerName,
    medical_facility: normalizedContext.medicalFacility,
    specialty,
    service_date: serviceDate,
    invoice_date: invoiceDate,
    total_amount: totalAmount,
    patient_paid_amount: patientPaidAmount,
    is_paid: paidFlag ?? patientPaidAmount > 0,
    payment_date: parseDateValue(row.payment_date),
    invoice_number: maybeText(row.invoice_number),
    secu_reimbursed: secuReimbursed,
    mutuelle_reimbursed: mutuelleReimbursed,
    notes: normalizedContext.description,
    metadata: {}
  }
}

function buildResilientSummaryInvoiceInput(
  row: Record<string, unknown>,
  rawRow: Record<string, unknown>
): Omit<MedicalInvoiceInput, 'patient_id'> | null {
  const headerBasedInput = buildSummaryInvoiceInput(row)

  if (headerBasedInput && headerBasedInput.specialty !== 'other') {
    return headerBasedInput
  }

  const candidates = collectRowCandidates(rawRow)
  const distinctDates = getDistinctDates(candidates)

  const serviceDate =
    parseDateValue(row.service_date) ||
    pickDateByHints(candidates, SERVICE_DATE_HEADER_HINTS) ||
    distinctDates[0] ||
    parseDateValue(row.invoice_date)
  const invoiceDate =
    parseDateValue(row.invoice_date) ||
    pickDateByHints(candidates, INVOICE_DATE_HEADER_HINTS) ||
    distinctDates.find((value) => value !== serviceDate) ||
    serviceDate

  if (!serviceDate || !invoiceDate) {
    return headerBasedInput
  }

  let practitionerName =
    maybeText(row.practitioner_name) ||
    pickTextByHints(candidates, PROVIDER_HEADER_HINTS)
  let medicalFacility =
    maybeText(row.medical_facility) ||
    pickTextByHints(candidates, FACILITY_HEADER_HINTS, practitionerName ? [practitionerName] : [])
  const description =
    maybeText(row.notes) ||
    pickTextByHints(
      candidates,
      NOTES_HEADER_HINTS,
      uniqueStrings([practitionerName, medicalFacility])
    ) ||
    pickFallbackText(candidates, uniqueStrings([practitionerName, medicalFacility]))

  const normalizedContext = normalizeSummaryContextFields({
    practitionerName,
    medicalFacility,
    description
  })

  if (!normalizedContext) {
    return headerBasedInput
  }

  practitionerName = normalizedContext.practitionerName
  medicalFacility = normalizedContext.medicalFacility

  const inferredSpecialtyText =
    maybeText(row.specialty) || pickTextByHints(candidates, SPECIALTY_HEADER_HINTS)

  const secuReimbursed =
    parseAmountValue(row.secu_reimbursed) ||
    pickAmountByHints(candidates, SECU_AMOUNT_HEADER_HINTS) ||
    0
  const mutuelleReimbursed =
    parseAmountValue(row.mutuelle_reimbursed) ||
    pickAmountByHints(candidates, MUTUELLE_AMOUNT_HEADER_HINTS) ||
    0
  const positiveAmounts = getPositiveAmounts(candidates)
  const largestAmount = positiveAmounts.length > 0 ? Math.max(...positiveAmounts) : null
  const totalAmount =
    parseAmountValue(row.total_amount) ??
    pickAmountByHints(candidates, TOTAL_AMOUNT_HEADER_HINTS) ??
    roundCurrency(
      (parseAmountValue(row.patient_paid_amount) ||
        pickAmountByHints(candidates, PATIENT_AMOUNT_HEADER_HINTS) ||
        0) +
      secuReimbursed +
      mutuelleReimbursed
    )
  const normalizedTotalAmount =
    isReasonableInvoiceAmount(totalAmount)
      ? totalAmount
      : isReasonableInvoiceAmount(largestAmount)
        ? largestAmount
        : null

  if (!normalizedTotalAmount) {
    return headerBasedInput
  }

  const patientPaidAmount =
    parseAmountValue(row.patient_paid_amount) ??
    pickAmountByHints(candidates, PATIENT_AMOUNT_HEADER_HINTS) ??
    roundCurrency(Math.max(normalizedTotalAmount - secuReimbursed - mutuelleReimbursed, 0))

  const paidFlag = inferBooleanValue(row.is_paid)
  const specialty = inferMedicalSpecialty(
    inferredSpecialtyText,
    practitionerName,
    medicalFacility,
    normalizedContext.description
  )

  return {
    practitioner_name: practitionerName,
    medical_facility: medicalFacility,
    specialty,
    service_date: serviceDate,
    invoice_date: invoiceDate,
    total_amount: normalizedTotalAmount,
    patient_paid_amount: patientPaidAmount,
    is_paid: paidFlag ?? patientPaidAmount > 0,
    payment_date: parseDateValue(row.payment_date),
    invoice_number:
      maybeText(row.invoice_number) || pickTextByHints(candidates, INVOICE_NUMBER_HEADER_HINTS),
    secu_reimbursed: secuReimbursed,
    mutuelle_reimbursed: mutuelleReimbursed,
    notes: normalizedContext.description,
    metadata: {}
  }
}

function parseDelimitedText(text: string, delimiter: string): string[][] {
  const rows: string[][] = []
  let currentRow: string[] = []
  let currentCell = ''
  let inQuotes = false
  const sanitizedText = text.replace(/^\uFEFF/, '')

  for (let index = 0; index < sanitizedText.length; index++) {
    const char = sanitizedText[index]
    const nextChar = sanitizedText[index + 1]

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        currentCell += '"'
        index++
      } else if (char === '"') {
        inQuotes = false
      } else {
        currentCell += char
      }
      continue
    }

    if (char === '"') {
      inQuotes = true
      continue
    }

    if (char === delimiter) {
      currentRow.push(currentCell)
      currentCell = ''
      continue
    }

    if (char === '\r') {
      continue
    }

    if (char === '\n') {
      currentRow.push(currentCell)
      rows.push(currentRow)
      currentRow = []
      currentCell = ''
      continue
    }

    currentCell += char
  }

  currentRow.push(currentCell)
  if (currentRow.some((value) => value.trim().length > 0)) {
    rows.push(currentRow)
  }

  return rows
}

function detectDelimitedSeparator(text: string): string {
  const candidates = [';', ',', '\t', '|']
  let bestDelimiter = ';'
  let bestScore = -1

  for (const delimiter of candidates) {
    const rows = parseDelimitedText(text, delimiter)
      .filter((row) => row.some((cell) => cell.trim().length > 0))
      .slice(0, 12)

    if (rows.length === 0) {
      continue
    }

    const columnCounts = rows.map((row) => row.length)
    const averageColumns =
      columnCounts.reduce((total, value) => total + value, 0) / columnCounts.length

    if (averageColumns <= 1) {
      continue
    }

    const spread = Math.max(...columnCounts) - Math.min(...columnCounts)
    const score = averageColumns * 10 - spread

    if (score > bestScore) {
      bestScore = score
      bestDelimiter = delimiter
    }
  }

  return bestDelimiter
}

function processExpenseSummaryMatrix(
  sheetName: string,
  matrix: unknown[][]
): {
  rows: ExpenseSummaryImportRow[]
  sheet: ExpenseSummarySheetPreview
  skippedRows: number
} {
  if (matrix.length === 0) {
    return {
      rows: [],
      sheet: { name: sheetName, importedRows: 0, skippedRows: 0 },
      skippedRows: 0
    }
  }

  const scanLimit = Math.min(matrix.length, 30)
  let headerRowIndex = 0
  let headerRowScore = -1

  for (let index = 0; index < scanLimit; index++) {
    const score = scoreHeaderRow(matrix[index])
    if (score > headerRowScore) {
      headerRowScore = score
      headerRowIndex = index
    }
  }

  const candidateHeaderRow = matrix[headerRowIndex] || []
  const treatFirstRowAsData =
    headerRowScore <= 1 && looksLikeInvoiceDataRow(candidateHeaderRow)
  const headers = treatFirstRowAsData
    ? buildSyntheticHeaders(getMaxColumnCount(matrix))
    : candidateHeaderRow.map((cell, index) => maybeText(cell) || `colonne_${index + 1}`)
  const dataStartIndex = treatFirstRowAsData ? 0 : headerRowIndex + 1
  const likelyInvoiceRows = countLikelyInvoiceRows(matrix, dataStartIndex)
  const shouldProcessSheet =
    (treatFirstRowAsData && likelyInvoiceRows >= 1) ||
    (headerRowScore >= 2 && likelyInvoiceRows >= 1) ||
    (headerRowScore >= 1 && isLikelyExpenseSheetName(sheetName) && likelyInvoiceRows >= 1) ||
    likelyInvoiceRows >= 3

  if (!shouldProcessSheet) {
    const nonEmptyRows = matrix.filter(
      (row) => row && row.some((value) => maybeText(value) !== null || typeof value === 'number')
    ).length

    return {
      rows: [],
      sheet: {
        name: sheetName,
        importedRows: 0,
        skippedRows: Math.max(nonEmptyRows - dataStartIndex, 0)
      },
      skippedRows: Math.max(nonEmptyRows - dataStartIndex, 0)
    }
  }

  const rows: ExpenseSummaryImportRow[] = []
  let sheetSkippedRows = 0

  for (let rowIndex = dataStartIndex; rowIndex < matrix.length; rowIndex++) {
    const rowValues = matrix[rowIndex]

    if (!rowValues || rowValues.every((value) => maybeText(value) === null && typeof value !== 'number')) {
      continue
    }

    if (!treatFirstRowAsData && (isProbablyTotalsRow(rowValues) || isRepeatedHeaderRow(rowValues))) {
      sheetSkippedRows++
      continue
    }

    const { mappedRow, rawRow } = buildStructuredRow(headers, rowValues)
    const input = buildResilientSummaryInvoiceInput(mappedRow, rawRow)

    if (!input) {
      sheetSkippedRows++
      continue
    }

    rows.push({
      input,
      sheetName,
      rowIndex: rowIndex + 1,
      rawRow
    })
  }

  return {
    rows,
    sheet: {
      name: sheetName,
      importedRows: rows.length,
      skippedRows: sheetSkippedRows
    },
    skippedRows: sheetSkippedRows
  }
}

export async function parseExpenseSummaryFile(file: File): Promise<ParsedExpenseSummaryFile> {
  const rows: ExpenseSummaryImportRow[] = []
  const sheets: ExpenseSummarySheetPreview[] = []
  let skippedRows = 0

  const isDelimitedFile =
    /\.csv$/i.test(file.name) ||
    file.type === 'text/csv' ||
    file.type === 'application/csv' ||
    file.type === 'text/plain'

  if (isDelimitedFile) {
    const rawText = await file.text()
    const delimiter = detectDelimitedSeparator(rawText)
    const matrix = parseDelimitedText(rawText, delimiter)
      .map((row) => row.map((cell) => cell.trim()))
      .filter((row) => row.some((cell) => cell.length > 0))
    const processed = processExpenseSummaryMatrix('CSV', matrix)

    rows.push(...processed.rows)
    sheets.push(processed.sheet)
    skippedRows += processed.skippedRows
  } else {
    const XLSX = await import('xlsx')
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, {
      type: 'array',
      raw: true,
      cellDates: true,
      dense: true
    })

    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName]
      const matrix = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: true,
        defval: null,
        blankrows: false
      }) as unknown[][]
      const processed = processExpenseSummaryMatrix(sheetName, matrix)

      rows.push(...processed.rows)
      sheets.push(processed.sheet)
      skippedRows += processed.skippedRows
    })
  }

  return {
    fileName: file.name,
    totalRows: rows.length,
    skippedRows,
    sheets,
    rows
  }
}

export async function saveMedicalInvoiceWithReconciliation(
  input: MedicalInvoiceInput,
  options: SaveMedicalInvoiceOptions
): Promise<SaveMedicalInvoiceResult> {
  const existingInvoices = options.existingInvoices || await fetchAllMedicalInvoices(input.patient_id)
  const matchingInvoice = findMatchingInvoice(existingInvoices, input)

  if (!matchingInvoice) {
    const payload: MedicalInvoiceInput = {
      ...input,
      metadata: buildBaseMetadata(input, options)
    }

    return {
      action: 'created',
      invoice: await createMedicalInvoice(payload),
      matchReason: null
    }

  }

  const mergedInput = buildMergedInvoiceInput(matchingInvoice.invoice, input, options)
  const changedFields = collectChangedFields(matchingInvoice.invoice, mergedInput)

  if (Object.keys(changedFields).length === 0) {
    return {
      action: 'duplicate',
      invoice: matchingInvoice.invoice,
      matchReason: matchingInvoice.reason
    }
  }

  return {
    action: 'updated',
    invoice: await updateMedicalInvoice(matchingInvoice.invoice.id, changedFields),
    matchReason: matchingInvoice.reason
  }

}

export async function importExpenseSummaryRows(args: {
  patientId: string
  parsedFile: ParsedExpenseSummaryFile
}): Promise<ExpenseSummaryImportResult> {
  const { patientId, parsedFile } = args
  const workingInvoices = await fetchAllMedicalInvoices(patientId)
  const invoices: MedicalInvoice[] = []
  const errors: string[] = []
  let createdCount = 0
  let updatedCount = 0
  let duplicateCount = 0

  for (const row of parsedFile.rows) {
    try {
      const result = await saveMedicalInvoiceWithReconciliation(
        {
          patient_id: patientId,
          ...row.input,
          metadata: {
            source_summary_sheet: row.sheetName,
            source_summary_row: row.rowIndex
          }
        },
        {
          origin: 'summary',
          sourceFileName: parsedFile.fileName,
          sourceSheetName: row.sheetName,
          sourceRowIndex: row.rowIndex,
          rawSourceRow: row.rawRow,
          existingInvoices: workingInvoices
        }
      )

      invoices.push(result.invoice)

      const existingIndex = workingInvoices.findIndex((invoice) => invoice.id === result.invoice.id)
      if (existingIndex >= 0) {
        workingInvoices[existingIndex] = result.invoice
      } else {
        workingInvoices.push(result.invoice)
      }

      if (result.action === 'created') {
        createdCount++
      } else if (result.action === 'updated') {
        updatedCount++
      } else {
        duplicateCount++
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue'
      errors.push(`${row.sheetName} ligne ${row.rowIndex}: ${message}`)
    }
  }

  return {
    totalRows: parsedFile.totalRows,
    createdCount,
    updatedCount,
    duplicateCount,
    skippedCount: parsedFile.skippedRows,
    invoices,
    errors
  }
}

export async function deleteSummaryOnlyImports(args: {
  patientId: string
  sourceFileName?: string | null
}): Promise<{ deletedCount: number }> {
  const invoices = await fetchAllMedicalInvoices(args.patientId)
  const normalizedSourceFile = args.sourceFileName?.trim() || null
  let deletedCount = 0

  for (const invoice of invoices) {
    const metadata = extractMetadata(invoice)
    const summaryFiles = Array.isArray(metadata.source_summary_file_names)
      ? metadata.source_summary_file_names.filter((value): value is string => typeof value === 'string')
      : []
    const matchesSourceFile =
      !normalizedSourceFile ||
      metadata.source_file_name === normalizedSourceFile ||
      summaryFiles.includes(normalizedSourceFile)

    if (metadata.is_summary_only !== true || invoice.document_url || !matchesSourceFile) {
      continue
    }

    await deleteMedicalInvoice(invoice.id)
    deletedCount += 1
  }

  return { deletedCount }
}

